import { brand } from "@/constants/colors";
import { fonts } from "@/constants/fonts";
import { useEffect } from "react";
import { StyleSheet, View } from "react-native";
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from "react-native-reanimated";
import BodyView from "../layout/BodyView";
import ThemedText from "../core/ThemedText";

interface LoaderProps {
  text?: string;
  size?: "sm" | "md" | "lg";
  color?: string;
}

export default function Loader({ text, size = "md", color }: LoaderProps) {
  const dotColor = color || brand.primary.main;

  const getDotSize = () => {
    switch (size) {
      case "sm":
        return 6;
      case "lg":
        return 10;
      default:
        return 8;
    }
  };

  const dotSize = getDotSize();

  const dot1TranslateY = useSharedValue(0);
  const dot2TranslateY = useSharedValue(0);
  const dot3TranslateY = useSharedValue(0);

  useEffect(() => {
    const animationConfig = {
      duration: 600,
      easing: Easing.bezier(0.25, 0.1, 0.25, 1),
    };

    const createAnimation = (sharedValue: any) => {
      sharedValue.value = withRepeat(
        withTiming(-dotSize * 1.5, {
          ...animationConfig,

          easing: Easing.inOut(Easing.ease),
        }),
        -1,
        true
      );
    };

    createAnimation(dot1TranslateY);
    setTimeout(() => createAnimation(dot2TranslateY), 150);
    setTimeout(() => createAnimation(dot3TranslateY), 300);

    return () => {
      dot1TranslateY.value = 0;
      dot2TranslateY.value = 0;
      dot3TranslateY.value = 0;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dotSize]);

  const dot1Style = useAnimatedStyle(() => ({
    transform: [{ translateY: dot1TranslateY.value }],
  }));

  const dot2Style = useAnimatedStyle(() => ({
    transform: [{ translateY: dot2TranslateY.value }],
  }));

  const dot3Style = useAnimatedStyle(() => ({
    transform: [{ translateY: dot3TranslateY.value }],
  }));

  return (
    <BodyView style={styles.container}>
      <View style={styles.dotsContainer}>
        <Animated.View
          style={[
            styles.dot,
            {
              width: dotSize,
              height: dotSize,
              borderRadius: dotSize / 2,
              backgroundColor: dotColor,
              marginHorizontal: dotSize / 2,
            },
            dot1Style,
          ]}
        />
        <Animated.View
          style={[
            styles.dot,
            {
              width: dotSize,
              height: dotSize,
              borderRadius: dotSize / 2,
              backgroundColor: dotColor,
              marginHorizontal: dotSize / 2,
            },
            dot2Style,
          ]}
        />
        <Animated.View
          style={[
            styles.dot,
            {
              width: dotSize,
              height: dotSize,
              borderRadius: dotSize / 2,
              backgroundColor: dotColor,
              marginHorizontal: dotSize / 2,
            },
            dot3Style,
          ]}
        />
      </View>

      {text && (
        <ThemedText
          style={[
            styles.text,
            {
              color: dotColor,
              fontSize: size === "sm" ? 12 : size === "lg" ? 16 : 14,
            },
          ]}
        >
          {text}
        </ThemedText>
      )}
    </BodyView>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    padding: 16,
  },
  dotsContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    height: 24,
  },
  dot: {
    backgroundColor: brand.primary.main,
  },
  text: {
    marginTop: 12,
    fontFamily: fonts.medium,
  },
});
